import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoBuscaMultipla } from '@/componentes/CampoBuscaMultipla';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { Barra } from '@/componentes/Esqueleto';
import { Seletor } from '@/componentes/Seletor';
import { EXEMPLO_DO_MOTIVO, faltaNoMotivo } from '@/lib/motivo';
import { useTanquesCadastrados } from '@/modulos/combustivel/consultas';

import { MensagemDeErro } from './Avisos';
import { usePessoas, useGravarTravaDoPapel } from './consultas';
import type { Papel, Pessoa } from './tipos';
import {
  corpoDaTrava,
  faltaNaTrava,
  formularioDoPapel,
  liberadasPorOutroPapel,
  pessoasComOPapel,
  travaDaPessoaEscrita,
  travaEscrita,
  type FormularioDaTrava,
  type TanquesDaTrava,
  type TipoDaTrava,
} from './trava-do-papel';

/**
 * 0128 · TRAVAR O LANÇAMENTO DE ABASTECIMENTO — a seção do papel, em Papéis.
 *
 * O dono (27/10/2026): *"quero configurar tudo isso direto em Papéis: travar
 * para que um papel permita só um tipo, e o tanque só um tanque"*. O papel nasce
 * sem trava; quem administra escolhe o tipo (interno, externo ou os dois) e os
 * tanques (todos, ou os marcados), escreve o motivo e grava. A trava vale para
 * todas as pessoas com o papel, no próximo lançamento delas.
 *
 * A FRASE QUE NÃO PODE FALTAR: a trava é do papel, e a pessoa com outro papel
 * que lança sem trava fica liberada. A seção diz quantas pessoas a trava
 * alcança e NOMEIA as que outro papel libera — quem trava precisa saber, ali,
 * que travou menos do que pensa.
 */
export function TravaDoLancamento({ papel, gere, leCombustivel }: { readonly papel: Papel; readonly gere: boolean; readonly leCombustivel: boolean }) {
  const tanques = useTanquesCadastrados(leCombustivel);
  const pessoas = usePessoas();
  const gravar = useGravarTravaDoPapel(papel);
  const [form, setForm] = useState<FormularioDaTrava>(() => formularioDoPapel(papel));
  const [motivo, setMotivo] = useState('');
  const [tentou, setTentou] = useState(false);

  const nomeDoTanque = (id: string) => {
    const t = (tanques.data ?? []).find((k) => k.id === id);
    return t ? `${t.nome} (${t.codigo})` : undefined;
  };
  const atual = travaEscrita(papel.abastecimento_tipos, papel.abastecimento_tanques, nomeDoTanque);
  const alcancadas = pessoasComOPapel(papel.id, pessoas.data?.papeis ?? [], pessoas.data?.itens ?? []);
  const liberadas = liberadasPorOutroPapel(papel, pessoas.data?.papeis ?? [], pessoas.data?.itens ?? []);
  const falta = faltaNaTrava(form);
  const faltaMotivo = faltaNoMotivo(motivo);
  const pedido = corpoDaTrava(form);
  const resumo = travaEscrita(pedido.tipos, pedido.tanques, nomeDoTanque);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="txt-subtitulo text-tinta">Travar o lançamento de abastecimento</h3>
        <p className="txt-corpo text-tinta">
          <span className="txt-rotulo-campo">Hoje: </span>
          {atual}
        </p>
        <p className="txt-apoio text-pretty text-tinta-secundaria">
          {`A trava vale para ${alcancadas === 1 ? 'a pessoa' : `as ${String(alcancadas)} pessoas`} com este papel, no próximo lançamento. Quem tiver outro papel que lança abastecimento sem trava continua liberado: a trava é a soma dos papéis da pessoa, e papel sem trava libera tudo.`}
        </p>
        {liberadas.length > 0 ? (
          /* O AVISO DE QUE A TRAVA NÃO PEGA, com os nomes: atenção (âmbar),
             porque não é erro — é a regra fazendo o que diz. */
          <p className="txt-apoio text-pretty rounded-controle bg-atencao-fundo px-4 py-2 text-atencao-texto">
            {`Outro papel libera o lançamento de ${liberadas.join(', ')}: para ${liberadas.length === 1 ? 'essa pessoa' : 'essas pessoas'}, esta trava não vale. Tire o outro papel, ou trave-o também.`}
          </p>
        ) : null}
      </div>

      {gere ? (
        <form
          className="flex flex-col gap-3 border-t border-borda-sutil pt-4"
          noValidate
          onSubmit={(evento) => {
            evento.preventDefault();
            setTentou(true);
            if (falta || faltaMotivo) return;
            gravar.mutate(
              { tipos: pedido.tipos, tanques: pedido.tanques, versao: papel.versao, motivo: motivo.trim(), resumo },
              {
                onSuccess: () => {
                  setMotivo('');
                  setTentou(false);
                },
              },
            );
          }}
        >
          <MensagemDeErro erro={gravar.error} />
          <div className="flex flex-col gap-2">
            <span className="txt-rotulo-campo">Tipo permitido</span>
            <Seletor<TipoDaTrava>
              rotulo="Tipo permitido"
              valor={form.tipo}
              opcoes={[
                /* "Os dois", curto: os três cabem no painel de 360 px. */
                { valor: 'ambos', rotulo: 'Os dois' },
                { valor: 'interno', rotulo: 'Só interno' },
                { valor: 'externo', rotulo: 'Só externo' },
              ]}
              aoEscolher={(tipo) => {
                setForm((f) => ({ ...f, tipo }));
              }}
            />
          </div>

          {form.tipo === 'externo' ? (
            <p className="txt-apoio text-tinta-secundaria">Só no posto, o tanque não se aplica.</p>
          ) : (
            <div className="flex flex-col gap-2">
              <span className="txt-rotulo-campo">Tanques permitidos</span>
              <Seletor<TanquesDaTrava>
                rotulo="Tanques permitidos"
                valor={form.tanques}
                opcoes={[
                  { valor: 'todos', rotulo: 'Todos os tanques' },
                  { valor: 'marcados', rotulo: 'Só os marcados' },
                ]}
                aoEscolher={(qual) => {
                  setForm((f) => ({ ...f, tanques: qual }));
                }}
              />
              {form.tanques === 'todos' ? null : !leCombustivel ? (
                <p className="txt-apoio text-tinta-secundaria">
                  Sem acesso aos tanques: a lista é do combustível. Peça a licença de ler o combustível a quem administra a empresa.
                </p>
              ) : tanques.isPending ? (
                <div className="flex flex-col gap-2" aria-busy="true">
                  <span className="sr-only">Carregando os tanques</span>
                  <Barra className="h-10 w-full rounded-controle" />
                  <Barra className="h-10 w-3/4 rounded-controle" />
                </div>
              ) : tanques.isError ? (
                <p className="txt-apoio text-erro-texto">Não foi possível carregar os tanques. Feche o papel e abra de novo.</p>
              ) : (
                <CampoBuscaMultipla
                  rotulo="Tanques"
                  opcoes={(tanques.data ?? [])
                    .filter((t) => t.ativo || form.marcados.includes(t.id))
                    /* O produto em etiqueta neutra, como no lançamento (31/10/2026). */
                    .map((t) => ({
                      valor: t.id,
                      rotulo: t.nome,
                      etiquetas: [{ texto: t.produto, tom: 'neutro' as const, titulo: 'Produto' }],
                      detalhe: t.codigo,
                    }))}
                  marcados={form.marcados}
                  aoMudar={(marcados) => {
                    setForm((f) => ({ ...f, marcados }));
                  }}
                  vazio="Nenhum tanque cadastrado. Cadastre o tanque no combustível antes de travar o papel nele."
                />
              )}
              {tentou && falta ? <p role="alert" className="txt-apoio text-erro-texto">{falta}</p> : null}
            </div>
          )}

          <CampoDoMotivo valor={motivo} aoMudar={setMotivo} exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO['papel.travar_abastecimento']}`} />
          {tentou && faltaMotivo ? <p role="alert" className="txt-apoio text-erro-texto">{faltaMotivo}</p> : null}

          <div className="flex justify-end">
            <Botao nivel="secundario" type="submit" disabled={gravar.isPending}>
              {gravar.isPending ? 'Gravando a trava…' : 'Salvar trava'}
            </Botao>
          </div>
        </form>
      ) : (
        <p className="txt-apoio text-tinta-secundaria">Quem trava o lançamento do papel é o perfil Administrador.</p>
      )}
    </div>
  );
}

/**
 * 0128 · O QUE VALE PARA A PESSOA, na aba Papéis dela: a trava que o banco
 * calcula (a soma dos papéis dela que lançam), e — quando um papel trava e
 * outro libera — o nome do que libera. Sem isso, quem olha a pessoa vê o papel
 * travado na lista e conclui que ela está travada.
 */
export function TravaDaPessoa({
  pessoa,
  papeisDaPessoa,
  leCombustivel,
}: {
  readonly pessoa: Pick<Pessoa, 'trava_do_lancamento'>;
  /** Os papéis VIGENTES da pessoa, com a trava de cada um. */
  readonly papeisDaPessoa: readonly Pick<Papel, 'nome' | 'lanca_abastecimento' | 'abastecimento_tipos' | 'abastecimento_tanques'>[];
  readonly leCombustivel: boolean;
}) {
  const tanques = useTanquesCadastrados(leCombustivel);
  const nomeDoTanque = (id: string) => {
    const t = (tanques.data ?? []).find((k) => k.id === id);
    return t ? `${t.nome} (${t.codigo})` : undefined;
  };
  const escrita = travaDaPessoaEscrita(pessoa.trava_do_lancamento, papeisDaPessoa, nomeDoTanque);
  if (escrita === null) return null;
  return (
    <div className="flex flex-col gap-1 border-t border-borda-sutil pt-4">
      <p className="txt-corpo text-tinta">
        <span className="txt-rotulo-campo">Lançamento de abastecimento: </span>
        {escrita.texto}
      </p>
      {escrita.aviso ? (
        <p className="txt-apoio text-pretty rounded-controle bg-atencao-fundo px-4 py-2 text-atencao-texto">{escrita.aviso}</p>
      ) : (
        <p className="txt-apoio text-pretty text-tinta-secundaria">
          É a soma dos papéis da pessoa que lançam abastecimento. Quem trava é o papel, em Papéis.
        </p>
      )}
    </div>
  );
}
